import React, { useEffect, useState } from 'react';
import { Empty } from 'antd';
import {
  Banner,
  Button,
  ButtonGroup,
  ComboRadio,
  CurrencyInput,
  Drawer,
  DrawerTabs,
  ErrorHandler,
  Gap,
  History,
  Input,
  Select,
  Text,
  Form,
} from '@common/components';
import { useMutation, useQuery } from '@common/hooks';
import { useCurrentMembership } from '@identity/hooks';
import { centsToDollars, dollarsToCents } from '@common/services';
import { createMessage } from 'works/legacy/lib/message-helper';
import {
  UPDATE_WORKER_SCHEDULE_CODE_MUTATION,
  WORKER_SCHEDULE_CODE_QUERY,
} from '@scheduling/graphql';

const CONSTRAINT_TYPE_OPTIONS = [
  { label: 'Informational (no constraint)', value: 'informational' },
  { label: 'Soft block (penalized but allowed)', value: 'soft_block' },
  { label: 'Hard block (cannot schedule over)', value: 'hard_block' },
];

const YES_NO_OPTIONS = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
];

// Incentive caps differ by org pay-code amount type: a tighter per-hour cap for
// most orgs, a higher cap for lump-sum (money) orgs like Mayo. The model
// enforces the higher value ($2,000) as an absolute backstop.
const MAX_INCENTIVE_PER_HOUR = 100;
const MAX_INCENTIVE_LUMP_SUM = 2000;

type ScheduleCodeFormValuesType = {
  displayName: string;
  description: string;
  fteContributing: boolean;
  fatigueContributing: boolean;
  constraintType: string;
  // Dollars, as entered. Stored server-side in cents.
  incentive: number | null;
};

// Maps the boolean form value onto ComboRadio's yes/no options (and back), so
// the form store keeps clean booleans.
const booleanRadioItemProps = {
  getValueProps: (checked: boolean) => ({ value: checked ? 'yes' : 'no' }),
  normalize: (value: string) => value === 'yes',
};

export function AdminScheduleCodeDrawer({
  open,
  onClose,
  codeId,
}: {
  open: boolean;
  onClose: () => void;
  codeId?: string;
}) {
  const [form] = Form.useForm<ScheduleCodeFormValuesType>();
  const [mutationErrors, setMutationErrors] = useState<
    { attribute: string; errors: string[] }[]
  >([]);

  // Members with worker_schedule_code_index but not _update get a read-only
  // drawer: every field disabled and no action buttons. The mutation is
  // permission-gated server-side regardless.
  const currentMembership = useCurrentMembership();
  const canUpdate = currentMembership?.canUpdateAdminScheduleCodes ?? false;

  const { data, error, loading } = useQuery(WORKER_SCHEDULE_CODE_QUERY, {
    variables: { id: codeId ?? '' },
    skip: !codeId,
    fetchPolicy: 'cache-and-network',
  });

  const code = data?.workerScheduleCode;

  useEffect(() => {
    if (!code) return;

    form.setFieldsValue({
      displayName: code.displayName ?? '',
      description: code.description ?? '',
      fteContributing: code.fteContributing,
      fatigueContributing: code.fatigueContributing,
      constraintType: code.constraintType,
      incentive: centsToDollars(code.incentive),
    });
  }, [code, form]);

  const [updateCode, { loading: saving }] = useMutation(
    UPDATE_WORKER_SCHEDULE_CODE_MUTATION,
    {
      onCompleted: (result) => {
        const errors = result?.updateWorkerScheduleCode?.errors;
        if (errors && errors.length > 0) {
          setMutationErrors(
            errors.map((entry) => ({
              attribute: entry.attribute,
              errors: [...entry.errors],
            }))
          );
          return;
        }
        createMessage('success', 'Schedule code saved');
        onDrawerClose();
      },
      onError: () =>
        createMessage(
          'error',
          "Schedule code couldn't be saved at this time. Try again later or contact support."
        ),
      refetchQueries: ['WorkerScheduleCodes', 'WorkerScheduleCode'],
    }
  );

  function onDrawerClose() {
    form.resetFields();
    setMutationErrors([]);
    onClose();
  }

  const isPaycode = code?.sourceType === 'paycode';

  // Lump-sum (money) orgs treat the incentive as a flat amount; others per-hour.
  const isLumpSum = data?.moneyPayCodesEnabled ?? false;
  const maxIncentive = isLumpSum
    ? MAX_INCENTIVE_LUMP_SUM
    : MAX_INCENTIVE_PER_HOUR;

  const incentiveDollars = Form.useWatch('incentive', form);
  const incentiveInvalid =
    isPaycode &&
    typeof incentiveDollars === 'number' &&
    (incentiveDollars < 0 || incentiveDollars > maxIncentive);

  const onSave = () => {
    if (!codeId || incentiveInvalid) return;
    setMutationErrors([]);

    // `true` also returns values seeded by setFieldsValue for fields that are
    // not rendered for this org/code (constraint type, incentive).
    const values = form.getFieldsValue(true) as ScheduleCodeFormValuesType;

    updateCode({
      variables: {
        id: codeId,
        displayName: values.displayName?.trim() || null,
        description: values.description?.trim() || null,
        fteContributing: values.fteContributing,
        fatigueContributing: values.fatigueContributing,
        constraintType: values.constraintType,
        // Incentive only applies to paycodes (the model enforces this too).
        incentive: isPaycode ? dollarsToCents(values.incentive ?? null) : null,
      },
    });
  };

  const detailsTab = (
    <Gap size="md" vertical fullWidth>
      {mutationErrors.map((entry) => (
        <Banner key={entry.attribute} type="error">
          {entry.errors.join(' ')}
        </Banner>
      ))}

      <Form layout="vertical" form={form} disabled={!canUpdate}>
        <Form.Item label={<Text bold>Type</Text>}>
          <Text>{isPaycode ? 'Pay code' : 'Work rule'}</Text>
        </Form.Item>

        <Form.Item label={<Text bold>Display name</Text>} name="displayName">
          <Input placeholder="Human-readable label" />
        </Form.Item>

        <Form.Item label={<Text bold>Description</Text>} name="description">
          <Input placeholder="What this code is used for" />
        </Form.Item>

        <Form.Item label={<Text bold>FTE Contributing</Text>}>
          <Gap size="sm" vertical fullWidth>
            <Text size="md" color="midnightRegular">
              Count hours with this code toward FTE attainment and incentive
              eligibility.
            </Text>
            <Form.Item
              name="fteContributing"
              noStyle
              {...booleanRadioItemProps}
            >
              <ComboRadio options={YES_NO_OPTIONS} />
            </Form.Item>
          </Gap>
        </Form.Item>

        <Form.Item label={<Text bold>Fatigue Contributing</Text>}>
          <Gap size="sm" vertical fullWidth>
            <Text size="md" color="midnightRegular">
              Count hours with this code toward fatigue caps (weekly / rolling
              7-day / consecutive hours).
            </Text>
            <Form.Item
              name="fatigueContributing"
              noStyle
              {...booleanRadioItemProps}
            >
              <ComboRadio options={YES_NO_OPTIONS} />
            </Form.Item>
          </Gap>
        </Form.Item>

        {isPaycode && (
          <Form.Item
            label={<Text bold>Incentive</Text>}
            name="incentive"
            extra={
              <Text
                size="md"
                color={incentiveInvalid ? 'coralDarker' : 'midnightRegular'}
              >
                {incentiveInvalid
                  ? `Enter ${
                      isLumpSum ? 'a lump-sum' : 'an hourly'
                    } incentive between $0.00 and $${maxIncentive.toFixed(2)}.`
                  : `${
                      isLumpSum ? 'Lump-sum' : 'Hourly'
                    } dollar incentive paid for this pay code. Leave blank for a non-incentive pay code.`}
              </Text>
            }
          >
            <CurrencyInput
              max={maxIncentive}
              addonAfter={isLumpSum ? undefined : '/hr'}
              placeholder="0.00"
            />
          </Form.Item>
        )}

        {data?.schedulingLoadBalancingEnabled && (
          <Form.Item label="Balancing Constraint Type" name="constraintType">
            <Select options={CONSTRAINT_TYPE_OPTIONS} />
          </Form.Item>
        )}
      </Form>
    </Gap>
  );

  const historyTab = code?.history?.length ? (
    <History data={[...code.history]} />
  ) : (
    <Empty description="No changes recorded yet." />
  );

  const tabs = [
    { title: 'Details', content: detailsTab },
    { title: 'History', content: historyTab },
  ];

  const footer = (
    <ButtonGroup>
      <Button onClick={onDrawerClose}>Cancel</Button>
      <Button
        kind="primary"
        onClick={onSave}
        loading={saving}
        disabled={!code || incentiveInvalid}
      >
        Save
      </Button>
    </ButtonGroup>
  );

  return (
    <Drawer
      open={open}
      className="admin-schedule-code-drawer"
      title={code?.code ?? 'Schedule code'}
      footer={canUpdate ? footer : undefined}
      onClose={onDrawerClose}
      loading={loading}
    >
      {error ? (
        <ErrorHandler error={error} log="admin-schedule-code-drawer" />
      ) : (
        <DrawerTabs tabs={tabs} />
      )}
    </Drawer>
  );
}
